<template>
  <canvas width="256" height="144" ref="canvas" />
</template>

<script>
import LayoutDrawer from '@wochitnpm/rcp/dist/_/LayoutDrawer';
import Html2Canvas from '@wochitnpm/rcp/dist/_/Html2Canvas';
import { RatioChangeFixHandler } from '@wochitnpm/adjustasset';
import app from '#/app';

const RCFH = new RatioChangeFixHandler();

export default {
  name: 'overlay-thumbnail',

  props: {
    backgroundColor: { type: String, default: '#fff' },
    main: { type: Object },
    overlay: { type: Object, require: true },
    aspectRatio: { type: Number, default: 16 / 9 },
    trimCurtains: { type: Boolean, default: false },
  },

  data() {
    return {
      ctx: null,
      mainAsset: null,
      overlayAssets: [],
    };
  },

  computed: {
    twitterRegExp() {
      return new RegExp('^' + app.PROVIDERS.TWITTER.id + '$', 'i');
    },
  },

  async mounted() {
    this.ctx = this.$refs.canvas.getContext('2d');
    this.ctx.fillStyle = '#d8d8d8';
    this.ctx.fillRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);
    let assetsToRender = [];
    // add the overlay asset.
    assetsToRender.push({
      ...this.overlay,
      layout: app.getOverlayLayout(this.overlay.sceneLayout),
    });

    // if there is main asset add it.
    if (this.main) {
      assetsToRender.push({
        ...this.main,
        layout: app.getMainLayout(this.overlay.sceneLayout),
        agd: {
          ...this.main.agd,
          ...this.overlay.sceneAgd,
        },
        startTime:
          (this.main.startTime || 0) + (this.overlay.displayStartTime || 0),
      });
    }

    // sort the asset base on z-index.
    assetsToRender = assetsToRender.sort((a, b) => {
      return a.layout.zIndex > b.layout.zIndex ? 1 : -1;
    });

    // get the image or color card from the metadata.
    assetsToRender = await Promise.all(
      assetsToRender.map((asset) => {
        const { metadata, wallpaperMetaData, agd, layout, startTime } = asset;

        if (wallpaperMetaData) {
          return this.fromColorCard({
            agd,
            layout,
            cssStyleObj: app.computeBgFromColorData(
              wallpaperMetaData.wallpaperData
            ),
          });
        } else if (
          startTime !== undefined &&
          metadata.url &&
          metadata.url.match(/^.*\.mp4\b/)
        ) {
          return this.fromVideo({ agd, layout, metadata, startTime });
        } else {
          return this.fromImage({ agd, layout, metadata });
        }
      })
    );

    // render the asset to the canvas.
    assetsToRender.forEach((asset) => this.drawImage(asset));

    // clear aspect ratio
    this.clearCurtains();
  },

  methods: {
    drawImage({ elm, layout, agd, fitToWidth = false }) {
      let { scale, offset } = RCFH.AdjustAsset({
        originalRatio: elm,
        innerRatio: {
          width: this.ctx.canvas.width * (layout.right - layout.left),
          height: this.ctx.canvas.height * (layout.bottom - layout.top),
        },
        agd,
      });
      if (fitToWidth) {
        scale =
          ((elm.height / elm.width) * this.ctx.canvas.width) /
          this.ctx.canvas.height;
        offset = {
          left: 0,
          top: 0,
        };
      }
      const layoutDrawer = new LayoutDrawer({
        scale,
        offset,
        el: {
          width: elm.width || elm.videoWidth,
          height: elm.height || elm.videoHeight,
        },
        layout: this.aspectRatioGap(layout, this.aspectRatio),
        canvas: {
          width: this.ctx.canvas.width,
          height: this.ctx.canvas.height,
        },
      });
      // clear the rect before drawing
      // @link https://wochit.atlassian.net/browse/DEV-9114.
      this.ctx.clearRect(...layoutDrawer.drawImageParams.slice(4));
      this.ctx.drawImage(elm, ...layoutDrawer.drawImageParams);
    },

    fromColorCard({ cssStyleObj, agd, layout }) {
      const style =
        'width:' +
        this.$refs.canvas.width +
        'px;' +
        'height:' +
        this.$refs.canvas.height +
        'px;' +
        'background:' +
        cssStyleObj.background +
        ';';

      return new Html2Canvas({
        html: '<div style="' + style + '"/>',
        size: {
          w: this.$refs.canvas.width,
          h: this.$refs.canvas.height,
        },
      })
        .convert()
        .then((elm) => {
          return {
            elm,
            agd: {
              scale: 1,
              offset: {
                top: 0,
                left: 0,
              },
            },
            layout,
          };
        });
    },

    fromImage({ agd, layout, metadata }) {
      return new Promise((res) => {
        const elm = document.createElement('img');
        elm.crossOrigin = 'Anonymous';
        elm.onload = () => {
          res({
            elm,
            agd,
            layout,
            fitToWidth: metadata && metadata.assetType === 'WEBPAGE',
          });
        };
        if (metadata.providerId.match(this.twitterRegExp) && !metadata.url) {
          elm.src = app.TWITTER_PLACEHOLDER.URL_BIG;
        } else {
          elm.src =
            metadata.thumbnail256x144Url ||
            metadata.thumbnail160x90Url ||
            metadata.url;
        }
      });
    },

    fromVideo({ agd, layout, metadata, startTime }) {
      return new Promise((res) => {
        const elm = document.createElement('video');
        elm.crossOrigin = 'Anonymous';
        elm.muted = true;
        elm.oncanplaythrough = () => {
          elm.currentTime = startTime || 0;
          elm.oncanplaythrough = null;
          elm
            .play()
            .then(() => {
              elm.pause();
              res({
                elm,
                agd,
                layout,
              });
            })
            .catch((e) => {
              res({
                elm,
                agd,
                layout,
              });
            });
        };
        elm.src = metadata.url;
      });
    },

    clearCurtains() {
      const aspectRatioWidth = this.aspectRatio * this.ctx.canvas.height;
      const curtain = (this.ctx.canvas.width - aspectRatioWidth) / 2;

      if (this.trimCurtains) {
        const imageData = this.ctx.getImageData(
          curtain,
          0,
          aspectRatioWidth,
          this.ctx.canvas.height
        );
        this.ctx.canvas.width = aspectRatioWidth;
        this.ctx.putImageData(imageData, 0, 0);
      } else {
        this.ctx.clearRect(
          curtain + aspectRatioWidth,
          0,
          curtain,
          this.ctx.canvas.height
        );
        this.ctx.clearRect(0, 0, curtain, this.ctx.canvas.height);
      }
    },

    /**
     * this function is just like in rcp toolkit.aspectRatioGap
     * the reason i didn't import the toolkit and use is that
     * it didn't feel good to rcp toolkit here.
     */
    aspectRatioGap(
      { top, left, right, bottom, zIndex },
      aspectRatioGap,
      baseAspectRatio = 16 / 9
    ) {
      const gap = (baseAspectRatio - aspectRatioGap) / (2 * baseAspectRatio);
      right = right * (aspectRatioGap / baseAspectRatio) + gap;
      left = (left * aspectRatioGap) / baseAspectRatio + gap;
      return { top, left, right, bottom, zIndex };
    },
  },
};
</script>

<style scoped>
canvas {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  width: 100%;
  height: 100%;
  cursor: pointer;
  display: block;
  background: #000;
}

.-category:hover canvas {
  box-shadow: none;
}

.-category.-selected canvas {
  box-shadow: none;
}
</style>
